<template>
  <div
    :class="className"
    @keyup.esc="onEscape"
    ref="container">
    <slot name="link">
      <header-link
        class="header-menu__trigger"
        :id="id"
        :menu-sprite="true"
        :active="active"
        :is-main="true"
        @click.prevent="onTriggerClick" />
    </slot>
    <div
      class="header-menu__menu-wrapper"
      ref="wrapper">
      <slot v-if="active" />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import HeaderLink from "./header-link";
import events from "@/events";
import bemBlockModifiers from "@/utils/bem-block-modifiers";
import matchMedia from "@/utils/media";

export default {
  name: "HeaderMenu",
  components: {
    HeaderLink
  },
  props: {
    id: { type: String, default: null },
    eventName: { type: String, default: null },
    modifiers: { type: Array, default: () => [] }
  },
  computed: {
    ...mapState("header", ["activeMenu"]),
    className() {
      const modifiers = [...this.modifiers];
      modifiers.push(this.id);
      if (this.active) {
        modifiers.push("active");
      }
      return bemBlockModifiers("header-menu", modifiers);
    },
    active() {
      return this.activeMenu === this.id;
    }
  },
  watch: {
    active(value) {
      document.removeEventListener("click", this.onDocumentClick);
      if (value) {
        this.body && this.body.classList.add("header-layout");
        this.header && this.header.classList.add("header--menu-active");

        if (matchMedia("max-width", "m")) {
          this.body && this.body.classList.add("scroll-disabled");
        }

        setTimeout(() => {
          document.addEventListener("click", this.onDocumentClick);
        });
      }
      events.track(this.eventName || `header-20200624-header_${this.id}`, `1-${value ? "open" : "close"}`);
    },
    activeMenu(newValue) {
      if (!newValue) {
        this.body && this.body.classList.remove("header-layout");
        this.body && this.body.classList.remove("scroll-disabled");
        this.header && this.header.classList.remove("header--menu-active");
      }
    }
  },
  mounted() {
    this.body = document.querySelector("body");
    this.header = this.$refs.container.closest(".header");
  },
  destroyed() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    close() {
      this.$store.commit("header/closeMenu");
    },
    onTriggerClick() {
      if (!this.active) {
        this.$store.commit("header/openMenu", this.id);
      }
    },
    onDocumentClick(e) {
      this.$nextTick(() => {
        const list = e.target.closest(".header-menu__menu-wrapper");
        if (!list) {
          this.close();
        }
      });
    },
    onEscape() {
      this.close();
    }
  }
};
</script>

<style lang="scss">
.header-menu {
  $root: &;
  $padding-header-bottom: 1.75rem;
  $padding-header-top: 1.75rem;
  $height-header: 1rem;

  position: relative;

  display: flex;
  align-items: center;
  height: 100%;

  &__menu-wrapper {
    width: auto;

    @include media(max-width, m) {

      /* On mobile we want the menu to position just below the header
      while it has fixed position */
      &#{&} > * {
        top: $padding-header-bottom + $height-header + $padding-header-bottom;
      }
    }

    @include media(min-width, m) {
      position: absolute;
      top: 100%;
      right: auto;
      left: auto;

      width: auto;
      height: auto;
      margin-top: $padding-header-bottom;

      overflow: hidden;

      #{$root}--right & {
        right: 0;
      }

      #{$root}--full & {
        right: 0;
        left: 0;
      }

      #{$root}--discover & {
        margin-top: 0;
      }

      #{$root}--search & {
        width: 100%;
      }
    }
  }
}

/* stylelint-disable selector-no-qualifying-type, selector-nested-pattern */
body.header-layout {

  .header {
    z-index: $zindex-header-active;
  }

  @include media(min-width, m) {

    &:before {
      content: "";

      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: $zindex-header-layout;

      display: block;

      background: rgba($grey-dark, 0.7);
    }
  }
}
</style>
